import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/auth.css';

const LoadingOverlay = () => (
    <div 
        style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
        }}
    >
        <div className="text-center">
            <Spinner
                animation="border"
                style={{ width: '3rem', height: '3rem' }}
                role="status"
            />
            <h4 className="mt-3">Logging in...</h4>
        </div>
    </div>
);

const Signin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [shakeForm, setShakeForm] = useState(false);
    const [errorField, setErrorField] = useState(null);
    const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
    const [showRetryButton, setShowRetryButton] = useState(false);

    const { signInUser, signInWithGoogle, user, loading, pgUser, clearAuthError } = useAuth();
    const navigate = useNavigate();

    // Debug log on every render
    console.log('Signin component render state:', { user, loading, pgUser, showLoadingOverlay });

    // Handle successful login
    useEffect(() => {
        console.log('Auth state changed in Signin:', { user, loading, pgUser });
        
        // Only navigate if we have a user and we're not in a loading state
        if (user && !loading) {
            console.log('User detected and not loading, navigating to auth loading page');
            setShowLoadingOverlay(true);
            navigate('/auth-loading');
        } else if (!user && !loading && isLoading) {
            console.log('No user detected after sign-in attempt, resetting loading state');
            setIsLoading(false);
        }
    }, [user, loading, pgUser, navigate]);

    // Reset shake animation after it completes
    useEffect(() => {
        if (shakeForm) {
            const timer = setTimeout(() => {
                console.log('Resetting shake animation');
                setShakeForm(false);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [shakeForm]);

    const handleSignInError = (errorMessage) => {
        console.log('handleSignInError called with:', errorMessage);
        
        let newError;
        let newErrorField = null;
        
        if (!navigator.onLine) {
            newError = 'Unable to connect. Please check your internet connection and try again.';
        } else if (errorMessage.toLowerCase().includes('server error') || errorMessage.includes('503') || errorMessage.includes('500')) {
            newError = 'Unable to connect to the server. Please try again later or send a message to support@getautochef.com';
        } else if (errorMessage.toLowerCase().includes('email not confirmed') || errorMessage.toLowerCase().includes('email_not_confirmed')) {
            newError = 'Please check your email inbox and click the confirmation link before signing in';
            newErrorField = 'email';
        } else if (errorMessage.toLowerCase().includes('invalid login credentials')) {
            newError = 'Invalid email or password';
            newErrorField = 'password';
        } else if (errorMessage.toLowerCase().includes('email')) {
            newError = 'Please enter a valid email address';
            newErrorField = 'email';
        } else {
            newError = 'An unexpected error occurred. Please try again.';
        }
            
        console.log('Setting new error states:', { newError, newErrorField });
        setError(newError);
        setErrorField(newErrorField);
        setShakeForm(true);
        
        // Show retry button for server/network errors after a delay
        if (newError.includes('Unable to connect')) {
            setTimeout(() => {
                setShowRetryButton(true);
            }, 3000);
        }
    };

    const handleSignIn = async (e) => {
        e.preventDefault();
        console.log('handleSignIn started');
        
        // Reset states
        setIsLoading(true);
        setError('');  // Only clear error when submitting
        setErrorField(null);
        setShakeForm(false);
        
        console.log('Sign-in attempt started for email:', email);
        
        try {
            console.log('Calling signInUser...');
            const result = await signInUser(email, password);
            console.log('Sign-in result:', result);
            
            if (result.success) {
                console.log('Sign-in successful, waiting for auth state update');
                setShowLoadingOverlay(true);
                // The useEffect will handle navigation once user state updates
                // Fallback in case state update is delayed
                setTimeout(() => {
                    if (!user && isLoading) {
                        console.log('Fallback: No user state update after sign-in, forcing session check');
                        // Force a session check or refresh if needed
                        // This could be a call to a manual refresh function if available
                        setIsLoading(false);
                        setError('Sign-in timed out. Please try again.');
                    }
                }, 10000); // 10 seconds fallback
            } else {
                console.log('Sign-in failed, handling error');
                handleSignInError(result.error);
                setShakeForm(true);
                // Give time for the error animation to be seen
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        } catch (error) {
            console.error('Exception during sign in:', error);
            handleSignInError('An unexpected error occurred. Please try again.');
            setShakeForm(true);
            // Give time for the error animation to be seen
            await new Promise(resolve => setTimeout(resolve, 100));
        } finally {
            // This might be overridden by the fallback timeout
            setIsLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setIsGoogleLoading(true);
        setError('');
        
        try {
            const result = await signInWithGoogle();
            if (!result.success) {
                setError(result.error);
                setIsGoogleLoading(false);
            }
            // No need to navigate - the OAuth redirect will handle this
        } catch (error) {
            setError('An error occurred during Google sign in');
            console.error('Google sign in error:', error);
            setIsGoogleLoading(false);
        }
    };

    return (
        <>
            {showLoadingOverlay && <LoadingOverlay />}
            <Container fluid className="py-5 px-3">
                <Row className="justify-content-center m-0">
                    <Col md={6} lg={4}>
                        <div className="text-center mb-4">
                            <h2>Time to cook.</h2>
                        </div>
                        
                        <Form 
                            onSubmit={handleSignIn} 
                            className={`auth-form ${shakeForm ? 'shake-animation' : ''}`}
                        >
                            {/* Reserve space for error message to prevent layout shift */}
                            <div style={{ minHeight: '60px' }}>
                                {error && (
                                    <Alert 
                                        variant="danger" 
                                        className={shakeForm ? 'shake-animation' : ''}
                                    >
                                        <div className="d-flex flex-column align-items-center">
                                            <div>{error}</div>
                                            {showRetryButton && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    className="mt-2"
                                                    onClick={() => {
                                                        setShowRetryButton(false);
                                                        setError('');
                                                        clearAuthError();
                                                        window.location.reload();
                                                    }}
                                                >
                                                    Retry Connection
                                                </Button>
                                            )}
                                        </div>
                                    </Alert>
                                )}
                            </div>
                            
                            <Button 
                                className="google-btn w-100 mb-3"
                                onClick={handleGoogleSignIn}
                                disabled={isGoogleLoading || isLoading}
                            >
                                {isGoogleLoading ? (
                                    'Connecting to Google...'
                                ) : (
                                    <div className="d-flex align-items-center justify-content-center">
                                        <img 
                                            src="/images/google_logo.png" 
                                            alt="Google logo" 
                                            style={{ width: '20px', height: '20px', marginRight: '10px' }} 
                                        />
                                        Continue with Google
                                    </div>
                                )}
                            </Button>
                            
                            <div className="auth-divider">
                                <span>or</span>
                            </div>
                            
                            <Form.Group className="mb-3">
                                <Form.Label className="d-block text-start">Email</Form.Label>
                                <Form.Control
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    placeholder="Enter your email"
                                    className={`form-control-custom ${errorField === 'email' ? 'error-highlight' : ''}`}
                                    disabled={isLoading}
                                    autoComplete="email"
                                    onFocus={() => setErrorField(null)} // Only clear highlight, not error message
                                />
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label className="d-block text-start">Password</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    placeholder="Enter your password"
                                    className={`form-control-custom ${errorField === 'password' ? 'error-highlight' : ''}`}
                                    disabled={isLoading}
                                    autoComplete="current-password"
                                    onFocus={() => setErrorField(null)} // Only clear highlight, not error message
                                />
                            </Form.Group>

                            <Button 
                                variant="primary" 
                                type="submit" 
                                className="w-100 mb-3"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Logging in...
                                    </>
                                ) : 'Log in'}
                            </Button>

                            <div className="text-center">
                                Don't have an account? <a href="/signup" className="auth-link">Sign up</a>
                            </div>
                            <div className="mt-2 text-center">
                                <div 
                                    onClick={() => navigate('/password-reset', { state: { email } })}
                                    className="auth-link p-0"
                                    style={{ cursor: 'pointer' }}
                                >
                                    Forgot password?
                                </div>
                            </div>
                        </Form>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default Signin;
